import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "./i18n"; // Initialize i18n
import { initializeLanguage } from "./i18n";
import Login from "./pages/Login";
import LandingPage from "./pages/LandingPage";
import LanguageSelectionPage from "./pages/LanguageSelectionPage";
import AsiaLanguageSelectionPage from "./pages/AsiaLanguageSelectionPage";
import HomePage from "./pages/HomePage";
import ScenarioLanding from "./pages/ScenarioLanding";
import TranslatePage from "./pages/traveler/TranslatePage";
import EverydayTranslatePage from "./pages/EverydayTranslatePage";
import LiveHelpPage from "./pages/traveler/LiveHelpPage";
import FavoritesPage from "./pages/traveler/FavoritesPage";
import ProfilePage from "./pages/traveler/ProfilePage";
import PricingPage from "./pages/PricingPage";
import AgentsPage from "./pages/AgentsPage";
import AgentProfilePage from "./pages/AgentProfilePage";
import BookingConfirmationPage from "./pages/BookingConfirmationPage";
import AgentDashboard from "./pages/agent/AgentDashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import RefundPolicy from "./pages/legal/RefundPolicy";
import BetaProgramNotice from "./pages/legal/BetaProgramNotice";
import LanguageSwitcher from "./components/LanguageSwitcher";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// ============================================================
// URL TOKEN HANDLER - Handles mobile app handoff via ?token=JWT
// ============================================================
const TokenHandler = ({ login }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    
    if (urlToken && !processing) {
      setProcessing(true);
      handleTokenValidation(urlToken);
    }
  }, [searchParams]);

  const handleTokenValidation = async (urlToken) => {
    try {
      console.log('🔐 Validating URL token for mobile handoff...');
      
      const response = await axios.post(`${API}/auth/validate-token`, {
        token: urlToken
      });

      if (response.data.valid && response.data.token && response.data.user) {
        // Successful validation - login the user
        login(response.data.token, response.data.user);
        
        // Show success message
        toast.success('Welcome back! You have been logged in.');
        
        // Get redirect path from response, default to home
        const redirectPath = response.data.redirect || '/home';
        
        // Clear the token from URL and redirect
        const cleanPath = location.pathname === '/' ? redirectPath : location.pathname;
        navigate(cleanPath, { replace: true });
        
        console.log('✅ Mobile handoff successful, redirecting to:', cleanPath);
      } else {
        // Token validation failed
        toast.error(response.data.error || 'Session expired. Please log in again.');
        navigate('/login', { replace: true });
        console.log('❌ Token validation failed:', response.data.error);
      }
    } catch (error) {
      console.error('❌ Token validation error:', error);
      toast.error('Unable to restore session. Please log in.');
      navigate('/login', { replace: true });
    } finally {
      setProcessing(false);
    }
  };

  // Show loading state while processing token
  if (processing) {
    return (
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[100]">
        <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
          <div className="w-12 h-12 border-4 border-aviation border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-700 font-medium">Restoring your session...</p>
        </div>
      </div>
    );
  }

  return null;
};

// Global Language Switcher Wrapper - shows on all pages except language-select and landing first-visit
const GlobalLanguageSwitcher = () => {
  const location = useLocation();
  
  // Hide on language selection page and landing page (which has its own selector)
  const hiddenPaths = ['/language-select', '/asia-languages'];
  const isLandingFirstVisit = location.pathname === '/' && !localStorage.getItem('passportTalk_primaryLanguage');
  
  if (hiddenPaths.includes(location.pathname) || isLandingFirstVisit) {
    return null;
  }
  
  return (
    <div className="fixed top-4 right-4 z-50" data-testid="global-language-switcher">
      <LanguageSwitcher />
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  // Initialize language and direction on app startup
  useEffect(() => {
    initializeLanguage();
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-aviation text-xl font-outfit">Loading...</div>
      </div>
    );
  }

  // Check if user has selected a primary language
  const getPrimaryLanguage = () => localStorage.getItem('passportTalk_primaryLanguage');
  
  // Asian language codes
  const asianLanguages = ['zh', 'ja', 'ko', 'vi', 'th'];
  
  // Determine the correct home route based on language preference
  const getHomeRoute = () => {
    const primaryLanguage = getPrimaryLanguage();
    if (!primaryLanguage) {
      // No language selected yet - go to language selection
      return '/language-select';
    }
    if (primaryLanguage !== 'en') {
      // Non-English speakers (es, fr, ar, zh, ja, ko, vi, th) go directly to conversation mode
      return '/conversation';
    }
    // English speakers get full home experience
    return '/home';
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, API }}>
      <div className="App">
        <BrowserRouter>
          {/* Token Handler - processes ?token= URL parameter for mobile handoff */}
          <TokenHandler login={login} />
          
          {/* Global Language Switcher - visible on all pages */}
          <GlobalLanguageSwitcher />
          
          <Routes>
            {/* Public Routes - accessible without login */}
            <Route path="/" element={!user ? <LandingPage /> : <Navigate to={getHomeRoute()} />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/refunds" element={<RefundPolicy />} />
            <Route path="/beta" element={<BetaProgramNotice />} />
            <Route path="/pricing" element={<PricingPage />} />
            
            <Route path="/login" element={!user ? <Login /> : <Navigate to={getHomeRoute()} />} />
            
            {/* Language selection - accessible to authenticated users */}
            {user && <Route path="/language-select" element={<LanguageSelectionPage />} />}
            
            {/* Asia language selection - accessible to all (for pre-auth language selection) */}
            <Route path="/asia-languages" element={<AsiaLanguageSelectionPage />} />
            
            {user && user.role === "traveler" && (
              <>
                {/* Full home (for English speakers) */}
                <Route path="/home" element={<HomePage />} />
                
                {/* Travel mode routes */}
                <Route path="/scenarios" element={<ScenarioLanding />} />
                <Route path="/translate" element={<TranslatePage />} />
                <Route path="/live-help" element={<LiveHelpPage />} />
                
                {/* WhatsApp/Conversation mode - accessible to all */}
                <Route path="/conversation" element={<EverydayTranslatePage />} />
                
                {/* Live Agent Scheduling (English users only - enforced in component) */}
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/agents/:agentId" element={<AgentProfilePage />} />
                <Route path="/booking-confirmation" element={<BookingConfirmationPage />} />
                
                {/* Common routes */}
                <Route path="/favorites" element={<FavoritesPage />} />
                <Route path="/profile" element={<ProfilePage />} />
              </>
            )}
            
            {user && user.role === "agent" && (
              <>
                <Route path="/home" element={<Navigate to="/agent" />} />
                <Route path="/agent" element={<AgentDashboard />} />
              </>
            )}
            
            {user && user.role === "admin" && (
              <>
                <Route path="/home" element={<Navigate to="/admin" />} />
                <Route path="/admin" element={<AdminDashboard />} />
              </>
            )}
            
            <Route path="*" element={<Navigate to={user ? getHomeRoute() : "/"} />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
